import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import { inject, observer } from "mobx-react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";

import SelectSuggestUI from "@UI/SelectSuggestUI";
import TagUi from "@UI/TagUi";

import { isEmpty, differ } from "@utils";
import moment from "moment";

import "./style.scss";

const Logger = inject("mainStore")(
    observer(({ mainStore }) => {
        const { secondParam } = useParams();

        const offers = mainStore.getMainStore("suggest.offers_suggest");

        const vendors = mainStore.getMainStore("modified.vendors_suggest");
        const regionIds = mainStore.getMainStore("modified.regionIds_suggest");
        const cityIds = mainStore.getMainStore("modified.cityIds_suggest");
        const metrostationsIds = mainStore.getMainStore("modified.metrostationsIds_suggest");
        const sites = mainStore.getMainStore("modified.sites_suggest");
        const apisendings = mainStore.getMainStore("modified.apisendings_suggest");
        const categories = mainStore.getMainStore("modified.categories_suggest");

        const [countAll, setCountAll] = useState(0);
        const [options, setOptions] = useState([]);
        const [filter, setFilter] = useState(defaultFilters);
        const [user, setUser] = useState([]);

        useEffect(() => {
            getList(filter);
        }, []);

        const formatter = (key, val) => {
            let value = "-";
            const data = {
                vendorId: vendors,
                siteId: sites,
                categoriesIds: categories,
                apiSendingIds: apisendings,
                availableOn: sites,
                metroStationIds: metrostationsIds,
                regionIds,
                cityIds,
            };

            const list = data[key] || {};
            const found = list?.[val] ?? undefined;

            switch (key) {
                case "vendorId":
                case "siteId":
                case "categoriesIds":
                case "apiSendingIds":
                case "availableOn":
                case "regionIds":
                case "cityIds":
                case "metroStationIds":
                    if (found) return found.name || found.title || found.name_with_type || found.city || "-";

                    break;
                case "dateEnd":
                case "dateStart":
                    value = moment(val).format("DD.MM.YYYY HH:mm") || "-";
                    break;
                case "offerOptions":
                    value = val;
                    break;
                case "inputFields":
                    value = Object.values(val).map((iVal, iIdx) => (
                        <div key={iIdx} className="">
                            <b>{iIdx + 1}.</b> {JSON.stringify(iVal) || "-"}
                        </div>
                    ));
                    break;

                default:
                    break;
            }

            return value;
        };

        const getList = async (filter) => {
            let params = { ...filter };
            params.filters = { ...params.filters, dbName: "promo", method: "offers" };

            if (secondParam) {
                params.filters = { elementId: secondParam };
            }

            const reqLogs = await mainStore.getByUrl(`/logs/cms_logs/`, { ...params }, "", true);
            if (reqLogs && reqLogs.success) {
                setOptions(reqLogs.data);
                setCountAll(reqLogs.count);
            }

            if (!user.length) {
                // не так часто меняются, одного раза хватит запросить
                const respUser = await mainStore.getByUrl("/cms/users", { rows: 1000 }, "users");
                if (respUser && respUser.success) {
                    setUser(respUser.data);
                }
            }
        };

        const changeFilterObjectPath = (data) => {
            const filterData = data.reduce((acc, row) => {
                acc[row.path] = row.value;
                return acc;
            }, {});

            const getListObj = { ...filterData, sortOrder: "desc" };
            if (secondParam) getListObj.filters = { elementId: `${secondParam}` };

            setFilter((prev) => ({ ...prev, ...filterData }));
            getList(getListObj);
        };

        const userBodyTemplate = useCallback(
            (item) => {
                const userItem = user.find((el) => el._id === item.userId);
                if (!userItem) return null;

                const moscowTime = moment.utc(item.reqTime).utcOffset(180).format("DD.MM.YYYY HH:mm"); // делаем время по МСК

                return (
                    <div className="logging-offer__user-name">
                        <span>
                            <b>
                                {userItem.firstName} {userItem.lastName}
                            </b>
                        </span>
                        <span>{moscowTime} (МСК)</span>
                    </div>
                );
            },
            [user],
        );

        const offerBodyTemplate = useCallback(
            (item) => {
                let ids = [];
                if (item.reqMethod == "DELETE") {
                    ids = [item.prevStateElement];
                } else if (item?.reqBody?.elements) {
                    ids = item.reqBody.elements.map((i) => offers.find((f) => f._id == i));
                } else {
                    ids = [offers.find((i) => i._id == item.elementId)];
                }

                return ids.length ? (
                    <div className="logging-offer__user-name">
                        {ids.map((i, idx) => (
                            <span key={idx}>
                                <b>{idx + 1}</b>. {i?.title}
                            </span>
                        ))}
                    </div>
                ) : null;
            },
            [offers],
        );

        const fieldBodyTemplate = (item) => {
            const rawFieldItem = item?.difference || item?.reqCda || null;
            if (!rawFieldItem) return null;

            const keys = Object.keys(rawFieldItem).filter((key) => key !== "_id" && key !== "__v");

            return keys.map((key, idx) => {
                const currentValue = rawFieldItem[key];
                let value = nameKey?.[key]?.title || nameKey?.[key] || key;

                if (typeof currentValue === "object" && currentValue !== null) {
                    // объект, не массив
                    if (!Array.isArray(currentValue)) {
                        const difference = differ(currentValue, item?.prevStateElement?.[key]);

                        if (difference && !isEmpty(difference)) {
                            const diffKeys = Object.keys(difference)
                                .filter((dKey) => dKey !== "_id" && dKey !== "__v")
                                .sort((a, b) => (a === b ? 0 : a > b ? 1 : -1));

                            value = diffKeys.map((diffKey, dIdx) => (
                                <div key={dIdx}>
                                    <b>
                                        {idx + 1}.{dIdx + 1}
                                    </b>{" "}
                                    {nameKey?.[diffKey]?.title || nameKey?.[diffKey] || diffKey}
                                </div>
                            ));
                        } else {
                            value = null;
                        }
                    }

                    // массив объектов
                    if (Array.isArray(currentValue) && currentValue.length && typeof currentValue[0] === "object") {
                        value =
                            currentValue.reduce((acc, row, rIdx) => {
                                const rowKeys = Object.keys(row);
                                rowKeys.forEach((diffKey, dIdx) => {
                                    acc.push(
                                        <div key={`${rIdx}-${dIdx}`}>
                                            <b>{dIdx + 1}.</b> {value} / {nameKey?.[key]?.subTitles?.[diffKey] || diffKey}
                                        </div>,
                                    );
                                });
                                return acc;
                            }, []) || "-";
                    }
                }

                if (!value) return null;

                return (
                    <div className="logging-offer__field" key={idx}>
                        <b>{idx + 1}.</b> <div className="logging-offer__subfield">{value}</div>
                    </div>
                );
            });
        };

        const diffBodyTemplate = (item, col) => {
            const rawFieldItem = item?.difference || item?.reqCda || null;
            if (!rawFieldItem) return null;

            const keys = Object.keys(rawFieldItem).filter((key) => key !== "_id" && key !== "__v");

            return keys.map((key, idx) => {
                const fieldVal = rawFieldItem[key];
                const baseValue = col.field === "prev" ? item?.prevStateElement?.[key] : fieldVal;

                let returnValue = JSON.stringify(baseValue) || "-";

                if (["dateStart", "dateEnd"].includes(key)) {
                    returnValue = formatter(key, baseValue);
                }

                if (typeof fieldVal === "object" && fieldVal !== null) {
                    if (!Array.isArray(fieldVal)) {
                        const difference = col.field === "prev" ? differ(fieldVal, baseValue) : differ(item?.prevStateElement?.[key], baseValue);

                        if (difference && !isEmpty(difference)) {
                            const diffKeys = Object.keys(difference)
                                .filter((dKey) => dKey !== "_id" && dKey !== "__v")
                                .sort((a, b) => (a === b ? 0 : a > b ? 1 : -1));

                            returnValue = diffKeys.map((diffVal, dIdx) => (
                                <div key={dIdx}>
                                    <b>
                                        {idx + 1}.{dIdx + 1}
                                    </b>{" "}
                                    {JSON.stringify(difference[diffVal]) || "-"}
                                </div>
                            ));
                        } else {
                            returnValue = "-";
                        }
                    }

                    if (Array.isArray(fieldVal) && baseValue) {
                        if (Array.isArray(baseValue) && baseValue.length && typeof baseValue[0] === "object") {
                            returnValue =
                                baseValue.reduce((acc, row) => {
                                    acc.push(formatter(key, row));
                                    return acc;
                                }, []) || "-";
                        } else if (Array.isArray(baseValue)) {
                            returnValue =
                                baseValue.reduce((acc, row) => {
                                    acc += `${formatter(key, row)}, `;
                                    return acc;
                                }, "") || "-";
                        }
                    }
                }

                if (!returnValue) return null;

                return (
                    <div className="logging-offer__field" key={idx}>
                        <b>{idx + 1}.</b> <div className="logging-offer__subfield">{returnValue}</div>
                    </div>
                );
            });
        };

        const statusBodyTemplate = useCallback((item) => {
            const tagObj = getSeverity[item.method] || getSeverity[item.reqMethod] || null;
            return tagObj && <TagUi value={tagObj.method} severity={tagObj.severity} />;
        }, []);

        const cols = secondParam
            ? [
                  <Column key={0} field="user" header="Имя пользователя и время" body={userBodyTemplate} style={{ width: "15%" }} />,
                  <Column key={1} field="field" header="Суть изменений" body={fieldBodyTemplate} style={{ width: "15%" }} />,
                  <Column key={2} field="prev" header="Исходные состояние" body={diffBodyTemplate} style={{ width: "35%" }} />,
                  <Column key={3} field="next" header="Измененное состояние" body={diffBodyTemplate} style={{ width: "35%" }} />,
              ]
            : [
                  <Column key={0} field="action" header="Действие" body={statusBodyTemplate} />,
                  <Column key={1} field="user" header="Имя пользователя и время" body={userBodyTemplate} />,
                  <Column key={2} field="offer" header="Название оффера" body={offerBodyTemplate} />,
              ];

        return (
            <div className="logging-offer">
                {options.length && offers.length && user.length ? (
                    <>
                        <DataTable value={options} tableStyle={{ minWidth: "50rem" }} emptyMessage="Нет доступных изменений">
                            {cols.map((c) => c)}
                        </DataTable>

                        <div className="logging-offer__pagination">
                            <Paginator
                                first={filter.first}
                                rows={filter.rows}
                                page={0}
                                totalRecords={countAll}
                                onPageChange={(e) => {
                                    changeFilterObjectPath([{ path: `first`, value: e.first }]);
                                }}
                                alwaysShow={true}
                            />
                            <SelectSuggestUI
                                addClass="logging-offer__count"
                                value={filter.rows}
                                onChange={(e) => {
                                    changeFilterObjectPath([
                                        { path: `rows`, value: e.target.value },
                                        { path: `first`, value: 0 },
                                    ]);
                                }}
                                options={[
                                    { label: 10, value: 10 },
                                    { label: 20, value: 20 },
                                    { label: 50, value: 50 },
                                    { label: 100, value: 100 },
                                    { label: 200, value: 200 },
                                    { label: 500, value: 500 },
                                    { label: 1000, value: 1000 },
                                ]}
                                optionValue="label"
                                optionLabel="value"
                                filter={false}
                                showClear={false}
                                placeholder=""
                            />
                        </div>
                    </>
                ) : (
                    <span className="logging-offer__no-data">Нет данных</span>
                )}
            </div>
        );
    }),
);

const defaultFilters = {
    first: 0,
    rows: 10,
    sortField: "_id",
    sortOrder: "desc",
    filters: {
        elementId: "",
    },
};

const getSeverity = {
    POST: { method: "Создание", severity: "success" },
    PUT: { method: "Редактирование", severity: "warning" },
    update_many: { method: "Редактирование", severity: "warning" },
    DELETE: { method: "Удаление", severity: "danger" },
};

const nameKey = {
    vendorId: "Вендор",
    siteId: "Сайт на котором используется",
    active: "Активность",
    dateStart: "Дата начала оффера",
    dateEnd: "Дата окончания оффера",
    sortNum: "Сортировка",
    offerType: "Вариант выдачи",
    onePromo: "Единый промокод",
    bucketCode: "Корзина с промиками",
    activationLink: "Ссылка на активацию",
    regionIds: "Регионы",
    cityIds: "Города",
    metroStationIds: "Метро",
    formtemplates: "Шаблон",
    offerDetail: "Детальная информация оффера",
    offerOptions: "Опции оффера",
    apiSendingIds: "Api Sendings (Отправка по api)",
    risPoints: "Lazy баллы",
    risScore: "Баллы за активацию",
    weight: "Вес",
    categoriesIds: "Категории",
    withoutCode: "Без кода?",
    keywords: "Ключевые слова для поиска",
    cover: "Баннер десктоп (cover, 776×287)",
    coverMobile: "Баннер Мобилка (coverMobile, 592х394)",
    bannerLink: "Баннер для письма (1000х370)",
    coverSlider: "Баннер для Слайдера (coverSlider, 608х342)",
    coverDetail: "Баннер Деталка (coverDetail)",
    mailOptions: "Конфиг для писем",
    howToActivate: "Как активировать?",
    termsOfUse: "Правила пользования {{dateStart}} {{dateEnd}}",
    ordRisPromo: "ORD Ris Promo",
    offerText: "Текст для письма",
    aboutPartner: "О партнере",
    termsOfUseLink: "Ссылка на правила пользования",
    miniDisc: "Текст в модалке на рис промо",
    showInputsInModal: "Не отображать поля в модальном окне оффера",
    inputFields: {
        title: "Поле",
        subTitles: {
            title: "Заголовок",
            type: "Тип поля",
            key: "Ключ в базе (траснлит)",
            placeholder: "Placeholder",
            sort: "Сортировка",
            active: "Активность",
            isVerified: "Верифицировать",
            require: "Обязательное",
        },
    },
    type: "Тип поля",
    key: "Ключ в базе (траснлит)",
    placeholder: "Placeholder",
    sort: "Сортировка",
    isVerified: "Верифицировать",
    require: "Обязательное",
    modal: "Отображение",
    banner: "Баннер (477х370)",
    text: "Текст",
    content: "Вопросы",
    formTemplateId: "Вопросы ID",
    title: "Название оффера",
    code: "Код оффера",
    description: "Описание",
    availableOn: "Сайты где используется оффер",
    offerModalWindow: "Данные для модального окна без ссылки на активацию",
    archived: "Архивный",
    categoriesId: "Категории",
    alternativeSort: "Сортировка (Альтерантивная для аб теста витрины)",
    squareLogo: "Логотип квадратный (120x120)",
    nameTranslit: "Транслит",
    name: "Имя вендора",
    logo: "Logo",
    iconRound: "Логотип круглый (500x500)",
    formTemplate: "Шаблон",
    userFieldReq: "Поля для получения оффера юзером",
    repayedPromo: "Погашено (промо)",
    uploadedPromo: "Загружено (промо)",
    partnerLogo: "Лого партнера (partnerLogo)",
    reachGoalMetrika: "ReachGoal для метрики",
    ordCode: "ORD Code",
    miniDescription: "Мини описание",
    mailActivationLink: "Ссылка на активацию в письме",
    notSend: "Не отправлять письмо",
    surveyId: "ID опроса",
    offerTypeCPC: "CPC тип",
    rateIds: "Ставки",
};

export default Logger;
